import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Modal,
  Row,
  Col,
  Card,
  Select,
  Input,
  Checkbox,
  Button,
  Flex,
  Typography,
  Tag,
  Spin,
  Empty,
  Space,
} from "antd";
import {
  SearchOutlined,
  CheckSquareOutlined,
  CloseSquareOutlined,
} from "@ant-design/icons";
import { ProductAppliesTo } from "../../../../api/admin/webshop/Promotion";
import {
  WsProduct,
  WsProductVariant,
  useGetWsProductWithVariantsQuery,
} from "../../../../api/webshop/wsProductsApi";

const { Text, Title } = Typography;

const LIST_HEIGHT = 280;
const EXCLUDED_HEIGHT = 180;

const scrollBoxStyle: React.CSSProperties = {
  height: LIST_HEIGHT,
  overflowY: "auto",
  border: "1px solid #d9d9d9",
  borderRadius: 6,
  background: "#fff",
  padding: "4px 8px",
};

export interface ApplicableProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (
    appliesTo: ProductAppliesTo,
    selectedIds: string[],
    excludedIds: string[],
  ) => void;
  initialAppliesTo: ProductAppliesTo;
  initialSelectedIds: string[];
  initialExcludedIds: string[];
  products: WsProduct[];
  regionOptions: string[];
  tagOptions: string[];
}

function productLabel(p: WsProduct) {
  return p.productTitle || p.title || p.productName || p.productId;
}

function ProductVariantsPanel(props: {
  productId: string;
  productName: string;
  selectedVariantIds: string[];
  onToggleVariant: (variantId: string, checked: boolean) => void;
  onBatchToggleVariants: (variantIds: string[], select: boolean) => void;
  onVariantsLoaded: (productId: string, variantIds: string[]) => void;
  isViewOnly?: boolean;
}) {
  const {
    productId,
    productName,
    selectedVariantIds,
    onToggleVariant,
    onBatchToggleVariants,
    onVariantsLoaded,
    isViewOnly = false,
  } = props;

  const [searchQuery, setSearchQuery] = useState("");

  const { data, isLoading } = useGetWsProductWithVariantsQuery(productId, {
    skip: !productId,
  });

  const variants: WsProductVariant[] = data?.variants || [];

  useEffect(() => {
    if (variants.length > 0) {
      onVariantsLoaded(
        productId,
        variants.map((v) => v.variantId),
      );
    }
  }, [productId, variants, onVariantsLoaded]);

  const sortedAndFilteredVariants = useMemo(() => {
    const sorted = [...variants].sort((a, b) =>
      (a.internalSku ?? a.variantId ?? "").localeCompare(
        b.internalSku ?? b.variantId ?? "",
        undefined,
        { sensitivity: "base" },
      ),
    );
    if (!searchQuery.trim()) return sorted;
    const q = searchQuery.toLowerCase().trim();
    return sorted.filter(
      (v) =>
        (v.name && v.name.toLowerCase().includes(q)) ||
        (v.internalSku && v.internalSku.toLowerCase().includes(q)) ||
        (v.variantId && v.variantId.toLowerCase().includes(q)),
    );
  }, [variants, searchQuery]);

  const allVisibleVariantIds = useMemo(
    () => sortedAndFilteredVariants.map((v) => v.variantId),
    [sortedAndFilteredVariants],
  );

  const selectedCountInThisProduct = useMemo(() => {
    return variants.filter((v) => selectedVariantIds.includes(v.variantId))
      .length;
  }, [variants, selectedVariantIds]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" style={{ height: LIST_HEIGHT }}>
        <Spin tip="Loading variants..." />
      </Flex>
    );
  }

  if (variants.length === 0) {
    return (
      <Flex justify="center" align="center" style={{ height: LIST_HEIGHT }}>
        <Empty description="No variants found for this product." />
      </Flex>
    );
  }

  return (
    <Flex vertical gap={8}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
        <div>
          <Text strong style={{ fontSize: 13 }}>
            {productName}
          </Text>
          <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
            ID: {productId} • {variants.length} variants
          </Text>
        </div>
        {!isViewOnly && (
          <Tag color="orange">
            {selectedCountInThisProduct} of {variants.length} selected
          </Tag>
        )}
      </Flex>

      <Input
        placeholder="Filter variants by name or SKU..."
        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        allowClear
        size="small"
      />

      {!isViewOnly && (
        <Flex gap={8}>
          <Button
            size="small"
            icon={<CheckSquareOutlined />}
            onClick={() => onBatchToggleVariants(allVisibleVariantIds, true)}
          >
            Select All
          </Button>
          <Button
            size="small"
            icon={<CloseSquareOutlined />}
            onClick={() => onBatchToggleVariants(allVisibleVariantIds, false)}
          >
            Deselect All
          </Button>
        </Flex>
      )}

      <div style={scrollBoxStyle}>
        {sortedAndFilteredVariants.map((v) => {
          const isSelected = selectedVariantIds.includes(v.variantId);
          return (
            <div
              key={v.variantId}
              style={{
                padding: "6px 0",
                borderBottom: "1px solid #f5f5f5",
              }}
            >
              <Flex align="center" justify="space-between" gap={8}>
                <Flex align="center" gap={8} style={{ flex: 1, minWidth: 0 }}>
                  {!isViewOnly && (
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) =>
                        onToggleVariant(v.variantId, e.target.checked)
                      }
                    />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <Text
                      strong
                      style={{ fontSize: 12, display: "block" }}
                      ellipsis
                    >
                      {v.name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      SKU: {v.internalSku || v.variantId}
                    </Text>
                  </div>
                </Flex>
                <Space size={6}>
                  {v.price != null && (
                    <Text style={{ fontSize: 11 }}>${v.price}</Text>
                  )}
                  <Tag
                    color={v.status === "ACTIVE" ? "green" : "default"}
                    style={{ margin: 0, fontSize: 10, lineHeight: "16px" }}
                  >
                    {v.status || "ACTIVE"}
                  </Tag>
                </Space>
              </Flex>
            </div>
          );
        })}
      </div>
    </Flex>
  );
}

export default function ApplicableProductsModal(
  props: ApplicableProductsModalProps,
) {
  const {
    isOpen,
    onClose,
    onApply,
    initialAppliesTo,
    initialSelectedIds,
    initialExcludedIds,
    products,
    regionOptions,
    tagOptions,
  } = props;

  const [appliesTo, setAppliesTo] = useState<ProductAppliesTo>("ALL");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const [activeProductId, setActiveProductId] = useState<string | null>(null);
  const [variantIdsByProduct, setVariantIdsByProduct] = useState<
    Record<string, string[]>
  >({});

  const [productSearch, setProductSearch] = useState("");
  const [optionSearch, setOptionSearch] = useState("");
  const [excludedSearch, setExcludedSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setAppliesTo(initialAppliesTo || "ALL");
    setSelectedIds(initialSelectedIds || []);
    setExcludedIds(initialExcludedIds || []);
    setProductSearch("");
    setOptionSearch("");
    setExcludedSearch("");
    setVariantIdsByProduct({});
    setActiveProductId(null);
  }, [isOpen, initialAppliesTo, initialSelectedIds, initialExcludedIds]);

  const filteredProducts = useMemo(() => {
    if (!products) return [];
    if (!productSearch.trim()) return products;
    const q = productSearch.toLowerCase().trim();
    return products.filter(
      (p) =>
        (p.productName || p.title || p.productTitle || "")
          .toLowerCase()
          .includes(q) || p.productId.toLowerCase().includes(q),
    );
  }, [products, productSearch]);

  const optionList = useMemo(() => {
    if (appliesTo === "REGIONS") return regionOptions || [];
    if (appliesTo === "TAGS" || appliesTo === "COLLECTIONS") {
      return tagOptions || [];
    }
    return [];
  }, [appliesTo, regionOptions, tagOptions]);

  const filteredOptions = useMemo(() => {
    if (!optionSearch.trim()) return optionList;
    const q = optionSearch.toLowerCase().trim();
    return optionList.filter((item) => item.toLowerCase().includes(q));
  }, [optionList, optionSearch]);

  const filteredExcludedProducts = useMemo(() => {
    if (!products) return [];
    if (!excludedSearch.trim()) return products;
    const q = excludedSearch.toLowerCase().trim();
    return products.filter(
      (p) =>
        (p.productName || p.title || p.productTitle || "")
          .toLowerCase()
          .includes(q) || p.productId.toLowerCase().includes(q),
    );
  }, [products, excludedSearch]);

  const activeProduct = useMemo(() => {
    return products.find((p) => p.productId === activeProductId) || null;
  }, [products, activeProductId]);

  const handleToggleId = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, id])));
    } else {
      setSelectedIds((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleBatchToggle = (ids: string[], select: boolean) => {
    if (select) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
    } else {
      setSelectedIds((prev) => prev.filter((item) => !ids.includes(item)));
    }
  };

  const handleToggleExcludedId = (id: string, checked: boolean) => {
    if (checked) {
      setExcludedIds((prev) => Array.from(new Set([...prev, id])));
    } else {
      setExcludedIds((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleVariantsLoaded = useCallback(
    (productId: string, variantIds: string[]) => {
      setVariantIdsByProduct((prev) => {
        const existing = prev[productId];
        if (
          existing &&
          existing.length === variantIds.length &&
          existing.every((id, i) => id === variantIds[i])
        ) {
          return prev;
        }
        return { ...prev, [productId]: variantIds };
      });
    },
    [],
  );

  const getSelectedVariantCount = (productId: string) => {
    const variantIds = variantIdsByProduct[productId] || [];
    if (variantIds.length === 0) return 0;
    return variantIds.filter((id) => selectedIds.includes(id)).length;
  };

  const handleApply = () => {
    const finalIds = appliesTo === "ALL" ? [] : selectedIds;
    onApply(appliesTo, finalIds, excludedIds);
    onClose();
  };

  const renderProductRow = (p: WsProduct, options?: { showCheckbox?: boolean }) => {
    const showCheckbox = options?.showCheckbox ?? true;
    const isActive = activeProductId === p.productId;
    const selectedVariantCount = getSelectedVariantCount(p.productId);
    const hasSelectedVariants =
      appliesTo === "VARIANTS" && selectedVariantCount > 0;
    const isChecked =
      appliesTo === "VARIANTS"
        ? hasSelectedVariants
        : selectedIds.includes(p.productId);

    return (
      <div
        key={p.productId}
        onClick={() => setActiveProductId(p.productId)}
        style={{
          padding: "6px 8px",
          marginBottom: 4,
          borderRadius: 4,
          cursor: "pointer",
          background: isActive
            ? "rgba(255, 70, 37, 0.08)"
            : hasSelectedVariants
              ? "rgba(255, 70, 37, 0.04)"
              : "transparent",
          border: isActive
            ? "1px solid rgba(255, 70, 37, 0.4)"
            : hasSelectedVariants
              ? "1px solid rgba(255, 70, 37, 0.2)"
              : "1px solid transparent",
        }}
      >
        <Flex align="center" justify="space-between" gap={8}>
          <Flex align="center" gap={8} style={{ flex: 1, minWidth: 0 }}>
            {showCheckbox && (
              <Checkbox
                checked={isChecked}
                disabled={appliesTo === "VARIANTS"}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleToggleId(p.productId, e.target.checked)}
              />
            )}
            <div style={{ minWidth: 0 }}>
              <Text
                strong
                style={{
                  fontSize: 12,
                  color: isActive ? "rgb(255, 70, 37)" : "inherit",
                }}
                ellipsis
              >
                {productLabel(p)}
              </Text>
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                {p.productId}
              </Text>
            </div>
          </Flex>
          {appliesTo === "VARIANTS" && selectedVariantCount > 0 && (
            <Tag color="orange" style={{ margin: 0 }}>
              {selectedVariantCount} selected
            </Tag>
          )}
        </Flex>
      </div>
    );
  };

  return (
    <Modal
      title="Configure Applicable Products"
      open={isOpen}
      onCancel={onClose}
      width={920}
      destroyOnClose
      maskClosable={false}
      bodyStyle={{ maxHeight: "72vh", overflowY: "auto" }}
      footer={
        <Flex justify="space-between" align="center">
          <Text type="secondary" style={{ fontSize: 12 }}>
            {appliesTo === "ALL"
              ? "Applies to: All Products"
              : appliesTo === "PRODUCTS"
                ? `Selected: ${selectedIds.length} products`
                : appliesTo === "VARIANTS"
                  ? `Selected: ${selectedIds.length} variants`
                  : appliesTo === "REGIONS"
                    ? `Selected: ${selectedIds.length} regions`
                    : `Selected: ${selectedIds.length} tags`}
            {excludedIds.length > 0 && ` • ${excludedIds.length} excluded`}
          </Text>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleApply}
              style={{
                backgroundColor: "rgb(255, 70, 37)",
                borderColor: "rgb(255, 70, 37)",
              }}
            >
              Apply Selection
            </Button>
          </Space>
        </Flex>
      }
    >
      <Flex vertical gap={12} style={{ marginTop: 8 }}>
        <Flex gap={12} align="center">
          <Text strong>Applies To:</Text>
          <Select
            value={appliesTo}
            onChange={(val) => {
              setAppliesTo(val);
              setSelectedIds([]);
              setActiveProductId(null);
            }}
            style={{ width: 280 }}
            options={[
              { label: "All Products in Catalog", value: "ALL" },
              { label: "Selected Products", value: "PRODUCTS" },
              { label: "Selected Variants", value: "VARIANTS" },
              { label: "Regions", value: "REGIONS" },
              { label: "Tags", value: "TAGS" },
            ]}
          />
        </Flex>

        <Row gutter={16}>
          <Col span={12}>
            <Card
              size="small"
              bordered
              style={{ background: "#fafafa", borderRadius: 8 }}
            >
              {appliesTo === "ALL" && (
                <Flex
                  vertical
                  justify="center"
                  align="center"
                  gap={8}
                  style={{ height: LIST_HEIGHT + 72, textAlign: "center" }}
                >
                  <Title level={5} style={{ margin: 0 }}>
                    All Products Eligible
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    This promotion applies to all products. Use the excluded
                    products list below to opt specific items out.
                  </Text>
                </Flex>
              )}

              {(appliesTo === "TAGS" ||
                appliesTo === "REGIONS" ||
                appliesTo === "COLLECTIONS") && (
                <Flex vertical gap={8}>
                  <Text strong style={{ fontSize: 13 }}>
                    {appliesTo === "REGIONS"
                      ? "Select Regions"
                      : "Select Tags"}
                  </Text>
                  <Input
                    placeholder={
                      appliesTo === "REGIONS"
                        ? "Search regions..."
                        : "Search tags..."
                    }
                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                    value={optionSearch}
                    onChange={(e) => setOptionSearch(e.target.value)}
                    allowClear
                    size="small"
                  />
                  <Flex gap={8}>
                    <Button
                      size="small"
                      onClick={() => handleBatchToggle(filteredOptions, true)}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleBatchToggle(filteredOptions, false)}
                    >
                      Deselect All
                    </Button>
                  </Flex>
                  <div style={scrollBoxStyle}>
                    {filteredOptions.length === 0 ? (
                      <Empty
                        description={
                          appliesTo === "REGIONS"
                            ? "No regions found."
                            : "No tags found."
                        }
                      />
                    ) : (
                      filteredOptions.map((item) => (
                        <div key={item} style={{ padding: "6px 0" }}>
                          <Checkbox
                            checked={selectedIds.includes(item)}
                            onChange={(e) =>
                              handleToggleId(item, e.target.checked)
                            }
                          >
                            {item}
                          </Checkbox>
                        </div>
                      ))
                    )}
                  </div>
                </Flex>
              )}

              {(appliesTo === "PRODUCTS" || appliesTo === "VARIANTS") && (
                <Flex vertical gap={8}>
                  <Text strong style={{ fontSize: 13 }}>
                    {appliesTo === "VARIANTS"
                      ? "Choose a product, then select variants on the right"
                      : "Select Eligible Products"}
                  </Text>
                  <Input
                    placeholder="Search products by title or ID..."
                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    allowClear
                    size="small"
                  />
                  {appliesTo === "PRODUCTS" && (
                    <Flex gap={8}>
                      <Button
                        size="small"
                        onClick={() =>
                          handleBatchToggle(
                            filteredProducts.map((p) => p.productId),
                            true,
                          )
                        }
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          handleBatchToggle(
                            filteredProducts.map((p) => p.productId),
                            false,
                          )
                        }
                      >
                        Deselect All
                      </Button>
                    </Flex>
                  )}
                  <div style={scrollBoxStyle}>
                    {filteredProducts.length === 0 ? (
                      <Empty description="No products found." />
                    ) : (
                      filteredProducts.map((p) =>
                        renderProductRow(p, {
                          showCheckbox: appliesTo === "PRODUCTS" || appliesTo === "VARIANTS",
                        }),
                      )
                    )}
                  </div>
                </Flex>
              )}
            </Card>
          </Col>

          <Col span={12}>
            <Card
              size="small"
              bordered
              style={{ background: "#fafafa", borderRadius: 8 }}
            >
              {appliesTo === "VARIANTS" || appliesTo === "PRODUCTS" ? (
                activeProductId && activeProduct ? (
                  <ProductVariantsPanel
                    productId={activeProductId}
                    productName={productLabel(activeProduct)}
                    selectedVariantIds={selectedIds}
                    onToggleVariant={handleToggleId}
                    onBatchToggleVariants={handleBatchToggle}
                    onVariantsLoaded={handleVariantsLoaded}
                    isViewOnly={appliesTo === "PRODUCTS"}
                  />
                ) : (
                  <Flex
                    justify="center"
                    align="center"
                    style={{ height: LIST_HEIGHT + 72, textAlign: "center" }}
                  >
                    <Empty description="Select a product on the left to load its variants." />
                  </Flex>
                )
              ) : (
                <Flex
                  justify="center"
                  align="center"
                  style={{ height: LIST_HEIGHT + 72, textAlign: "center" }}
                >
                  <Empty
                    description={
                      appliesTo === "ALL"
                        ? "All products are eligible. Exclude items below if needed."
                        : appliesTo === "REGIONS"
                          ? "Select regions on the left. Exclude items below if needed."
                          : "Select tags on the left. Exclude items below if needed."
                    }
                  />
                </Flex>
              )}
            </Card>
          </Col>
        </Row>

        <Card
          size="small"
          bordered
          title={
            <Flex justify="space-between" align="center">
              <Text strong>Excluded Products (Optional)</Text>
              {excludedIds.length > 0 && (
                <Tag color="red">{excludedIds.length} excluded</Tag>
              )}
            </Flex>
          }
          style={{ background: "#fafafa", borderRadius: 8 }}
        >
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
            These products are excluded even if they would otherwise qualify.
          </Text>
          <Input
            placeholder="Search products to exclude..."
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={excludedSearch}
            onChange={(e) => setExcludedSearch(e.target.value)}
            allowClear
            size="small"
            style={{ marginBottom: 8 }}
          />
          <div
            style={{
              ...scrollBoxStyle,
              height: EXCLUDED_HEIGHT,
            }}
          >
            {filteredExcludedProducts.length === 0 ? (
              <Empty description="No products found." />
            ) : (
              filteredExcludedProducts.map((p) => (
                <div key={p.productId} style={{ padding: "5px 0" }}>
                  <Checkbox
                    checked={excludedIds.includes(p.productId)}
                    onChange={(e) =>
                      handleToggleExcludedId(p.productId, e.target.checked)
                    }
                  >
                    <Text strong style={{ fontSize: 12 }}>
                      {productLabel(p)}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                      ({p.productId})
                    </Text>
                  </Checkbox>
                </div>
              ))
            )}
          </div>
        </Card>
      </Flex>
    </Modal>
  );
}
